import CloseIcon from '@mui/icons-material/Close';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  IconButton,
  Box,
  Typography,
  MenuItem,
  Select,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import API from 'configs/API';
import { axiosPatch } from 'utils/apis/axios';
import { ToastTopHelper } from 'utils/utils';

const AddOwnerModal = ({ open, onClose, editionId, initialData, artworkId }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const resolvedInitial = useMemo(() => {
    const owner = initialData?.ownerName ?? initialData?.owner_name ?? '';
    const p = initialData?.price ?? '';
    const c = initialData?.currency ?? 'vnd';

    return {
      ownerName: owner,
      price: p,
      currency: typeof c === 'string' ? c.toLowerCase() : 'vnd',
    };
  }, [initialData]);

  const [ownerName, setOwnerName] = useState(resolvedInitial.ownerName);
  const [price, setPrice] = useState(resolvedInitial.price);
  const [currency, setCurrency] = useState(resolvedInitial.currency);

  useEffect(() => {
    if (!open) {
      return;
    }

    setOwnerName(resolvedInitial.ownerName);
    setPrice(resolvedInitial.price);
    setCurrency(resolvedInitial.currency);
  }, [open, resolvedInitial]);

  const updateEditionOwner = async (payload) => {
    const { data } = await axiosPatch(
      false,
      API.ARTWORK.EDITION_UPDATE_STATUS(payload.id),
      payload,
    );

    return data;
  };

  const { mutate: updateOwner, isPending } = useMutation({
    mutationFn: updateEditionOwner,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API.ARTWORK.EDITION] });
      if (artworkId) {
        queryClient.invalidateQueries({ queryKey: [API.ARTWORK.ARTWORK_DETAIL(artworkId)] });
      }
      ToastTopHelper.success(t('update_successfully'));
      onClose?.();
    },
  });

  const handleUpdate = () => {
    if (!editionId) {
      return;
    }

    updateOwner({
      id: editionId,
      owner_name: ownerName,
      currency: (currency || 'vnd').toLowerCase(),
      price: price,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          padding: 2,
        },
      }}
    >
      <DialogTitle sx={{ padding: 0, marginBottom: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="h6"
            fontWeight={600}
          >
            {t('add_owner')}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            disabled={isPending}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: 0 }}>
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
        >
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              marginBottom={1}
            >
              {t('owners_name')}
            </Typography>
            <TextField
              fullWidth
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              placeholder={t('enter_owner_name')}
              variant="outlined"
              size="small"
            />
          </Box>

          <Typography
            variant="body2"
            color="text.secondary"
            fontSize="13px"
          >
            {t('owner_name_helper_text')}
          </Typography>

          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              marginBottom={1}
            >
              {t('price')}
            </Typography>
            <Box
              display="flex"
              gap={1}
            >
              <TextField
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder={t('enter_price')}
                variant="outlined"
                size="small"
                type="text"
                sx={{ flex: 1 }}
              />
              <Select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  minWidth: 90,
                }}
              >
                <MenuItem value="vnd">VND</MenuItem>
                <MenuItem value="usd">USD</MenuItem>
              </Select>
            </Box>
          </Box>

          <Box
            display="flex"
            justifyContent="flex-end"
            marginTop={2}
          >
            <Button
              variant="contained"
              onClick={handleUpdate}
              disabled={isPending || !editionId}
              sx={{
                backgroundColor: '#000',
                color: '#fff',
                textTransform: 'none',
                paddingX: 4,
                paddingY: 1,
                '&:hover': {
                  backgroundColor: '#333',
                },
              }}
            >
              {t('update')}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddOwnerModal;
